/**
 * @voltxampere/components — Analog multiplexer (Sprint 43, ADR-0063)
 *
 * Sprint 43 introduces the `analog-mux-8` (4051 / 4052 / 74HC4051)
 * and `analog-mux-16` (4067) canonical component types. Both share
 * the same parameter schema; the type-string discriminator selects
 * channel + select-pin counts at the engine layer.
 *
 * Engine stamp: `packages/engine/src/stamps/analog-mux.ts`
 * (Sprint 4 voltage-source augmented row pattern reuse).
 *
 * Pin layout policy: each definition lists its pins explicitly so the
 * components-package validator (`validate.ts:63 — terminals.length
 * must equal pins.length`) accepts only fully-wired instances. A
 * 4051 instance therefore has 13 terminals; a 4067 instance has 22.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Shared parameter schema for both 8- and 16-channel analog mux
 * variants. Every field has a default that matches the TI CD4051B /
 * CD4067B datasheet; user-authored circuits can omit any of them.
 */
export const AnalogMuxParamsSchema = z
    .object({
    /**
     * Decoded channel count. Pinned per definition (8 for
     * `analog-mux-8`, 16 for `analog-mux-16`); included in the schema
     * so consumers (inspector card, future docs export) can read it
     * without round-tripping through the type string.
     */
    channelCount: z.union([z.literal(8), z.literal(16)]).default(8),
    /**
     * Boolean threshold applied to select pins. TI CMOS family at
     * Vcc=5 V uses ≈ 2.5 V midpoint.
     */
    threshold: z.number().finite().default(2.5),
    /**
     * Active polarity of the enable pin. 4051 / 4067 are active LOW;
     * a future variant could ship with `false` for active HIGH (e.g.
     * an inverted-enable wrapper IC).
     */
    enableActiveLow: z.boolean().default(true),
    /**
     * On-resistance approximation in ohms. Sprint 43 idealizes the
     * mux as R_ON = 0; the field is typed for forward compatibility
     * with the Sprint 50+ controlled-switch upgrade.
     */
    rOn: z.number().nonnegative().finite().default(100),
    /**
     * Propagation delay through the mux for the future transient
     * stamp. Currently unread by the DC stamp.
     */
    propagationDelay: z.number().nonnegative().finite().default(60e-9),
})
    .strict();
/**
 * Build a left-side pin list for the channel inputs ch0..chN-1. The
 * pins stack vertically, lowest channel at the top.
 */
function buildChannelPins(count) {
    const pins = [];
    for (let i = 0; i < count; i += 1) {
        pins.push({
            id: `ch${i.toString()}`,
            side: "left",
            offset: i,
            label: `CH${i.toString()}`,
        });
    }
    return pins;
}
/**
 * Build the right-side pin list — select pins, enable, common output.
 * The order is select(LSB→MSB) → enable → common, stacking down the
 * right side.
 */
function buildControlPins(selectPinCount) {
    const letters = ["a", "b", "c", "d"];
    const pins = [];
    for (let i = 0; i < selectPinCount; i += 1) {
        pins.push({
            id: `sel_${letters[i] ?? "?"}`,
            side: "right",
            offset: i,
            label: `S${i.toString()}`,
        });
    }
    pins.push({
        id: "enable",
        side: "right",
        offset: selectPinCount,
        label: "/E",
    });
    pins.push({
        id: "common",
        side: "right",
        offset: selectPinCount + 1,
        label: "Z",
    });
    return pins;
}
/** 8-channel analog mux (4051 / 4052 / 74HC4051). 13 pins total. */
export const analogMux8Definition = {
    type: "analog-mux-8",
    category: "active",
    displayName: { tr: "8 Kanal Analog Mux", en: "8-Channel Analog Mux" },
    symbol: "U",
    pins: [...buildChannelPins(8), ...buildControlPins(3)],
    parameterSchema: AnalogMuxParamsSchema,
    // The select-pin threshold makes the stamp non-smooth; flag as
    // nonlinear so Newton-Raphson iterates the augmented-row constraint
    // (mirrors `digital-gate`).
    nonlinear: true,
};
/** 16-channel analog mux (4067). 22 pins total. */
export const analogMux16Definition = {
    type: "analog-mux-16",
    category: "active",
    displayName: { tr: "16 Kanal Analog Mux", en: "16-Channel Analog Mux" },
    symbol: "U",
    pins: [...buildChannelPins(16), ...buildControlPins(4)],
    parameterSchema: AnalogMuxParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=analog-mux.js.map